import {
  ITooltipComp,
  ITooltipParams,
  PopupComponent,
} from '@ag-grid-community/core'

/** Static presentation, kept byte-identical to the previous markup. */
const TABLE_STYLE = 'border: 0.0625rem solid #adb5bd;'
const THEAD_STYLE =
  'border-bottom:0.1875rem solid grey;background-color:#e9ecef'
const CELL_STYLE = 'border-right: 0.0625rem solid; padding-left:0.25rem'
const BODY_ROW_STYLE = 'border-bottom:0.1275rem solid grey'

/**
 * Optional extra params a colDef may pass through `tooltipComponentParams` to
 * point this tooltip at a message map OTHER than the row's `errorMessageMap`.
 *
 * Added for the Submission column, which renders `submissionMessages` —
 * `result.messages` straight off the batch response, i.e. the SAME untrusted
 * server strings this class exists to neutralise. That column used to carry a
 * `tooltipValueGetter` and NO `tooltipComponent`, so those strings rendered
 * through ag-grid's DEFAULT tooltip component, and `createGridOptions()` spreads
 * only `heliosSizingAdjustments` so this grid does not inherit
 * `enableBrowserTooltips: true` from `heliosClientSideRowConfig` /
 * `heliosServerSideRowConfig` either. Redirecting the map — rather than teaching
 * this class about specific columns — keeps exactly one hardened tooltip on the
 * grid.
 */
export interface ProcessingTooltipParams {
  messageMap?: (params?: ITooltipParams) => Map<string, string[]> | undefined
}

/** The merged params ag-grid hands `init` when a colDef supplies a getter. */
type ResolvableTooltipParams = ITooltipParams & ProcessingTooltipParams

/**
 * Lib-local per-cell error tooltip for the Corporate Actions Processing grid.
 *
 * `libs/create-transactions-modal/src/index.ts` exports `./lib/util/common-utils`
 * (so `deduceValidityStatusCellClass` is public) but does NOT export
 * `lib/components/bulk-transaction/error-Tooltip/error-tooltip.component`. A deep
 * relative import would trip `@nrwl/nx/enforce-module-boundaries`, and widening
 * that lib's public API is out of scope for this ticket — hence this small
 * lib-local equivalent.
 *
 * Reads `errorMessageMap` (field -> messages) off the row node's data, exactly
 * as the bulk panels' tooltip does — or, when the colDef supplies a
 * `messageMap` getter through `tooltipComponentParams`, whatever that getter
 * returns (see `ProcessingTooltipParams`). Every column on this grid that shows
 * a tooltip goes through here, so there is exactly one escaping boundary to
 * maintain.
 *
 * ## Why this builds DOM nodes instead of an HTML string
 *
 * The table is assembled with `document.createElement` and every dynamic value
 * is written through `textContent` — never string-interpolated into markup.
 * This map is not always client-authored: on the submit path
 * `applyRowResults()` keys it by the batch response's `status` with the
 * response's `messages` as values, and both are open-typed (`status: string`,
 * `messages?: string[]`) and unvalidated. Interpolating those into
 * `innerHTML` — as the original implementation did, and as the pre-existing
 * `ErrorTooltipComponent` in `create-transactions-modal` still does — makes a
 * server string containing markup execute in the session-authenticated Helios
 * origin on hover. Angular's sanitizer does not cover it: this is direct DOM
 * assignment inside an ag-grid `PopupComponent`, outside any template binding.
 *
 * Keep it that way. If you need to add a row or a column here, create the
 * element and set `textContent`; do not reintroduce an `innerHTML` assignment.
 */
export class ProcessingErrorTooltip
  extends PopupComponent
  implements ITooltipComp
{
  constructor() {
    super(`<div class="ag-grid-tooltip tooltip show bs-tooltip-bottom">
    <div class="arrow"></div>
    <div class="tooltip-inner"></div>
</div>`)
  }

  /**
   * Build the tooltip table, or return `null` when there is nothing to show.
   *
   * Exported for unit testing. Returns an element rather than an HTML string
   * precisely so there is no string-concatenation seam a server-controlled
   * `field` or message could be injected through.
   */
  static buildTable(
    errorMessageMap?: Map<string, string[]>,
  ): HTMLTableElement | null {
    if (!errorMessageMap || errorMessageMap.size === 0) {
      return null
    }

    const table = document.createElement('table')
    table.setAttribute('style', TABLE_STYLE)

    const thead = document.createElement('thead')
    thead.setAttribute('style', THEAD_STYLE)
    const headerRow = document.createElement('tr')
    ;['Field', 'ValidationErrors'].forEach(label => {
      const th = document.createElement('th')
      th.setAttribute('scope', 'col')
      th.setAttribute('style', CELL_STYLE)
      th.textContent = label
      headerRow.appendChild(th)
    })
    thead.appendChild(headerRow)
    table.appendChild(thead)

    const tbody = document.createElement('tbody')
    errorMessageMap.forEach((messages: string[], field: string) => {
      const row = document.createElement('tr')
      row.setAttribute('style', BODY_ROW_STYLE)
      // textContent, not innerHTML: `field` and `messages` can both originate
      // from the batch response and are never trusted as markup.
      ;[field, (messages ?? []).join(' | ')].forEach(text => {
        const cell = document.createElement('td')
        cell.setAttribute('style', CELL_STYLE)
        cell.textContent = text
        row.appendChild(cell)
      })
      tbody.appendChild(row)
    })
    table.appendChild(tbody)

    return table
  }

  /**
   * The map this tooltip should render for `params`.
   *
   * Prefers a `messageMap` getter supplied through the colDef's
   * `tooltipComponentParams` (ag-grid merges those into the params handed to
   * `init`), otherwise falls back to the row's own `errorMessageMap` — so the
   * validity-status column and the per-cell columns behave exactly as before.
   *
   * Note the failure mode is fail-CLOSED: a getter that is missing or returns
   * nothing renders an empty tooltip, never an unescaped one. There is no path
   * through here that reaches an HTML sink.
   */
  static resolveMessageMap(
    params?: ITooltipParams,
  ): Map<string, string[]> | undefined {
    const merged = params as ResolvableTooltipParams | undefined
    const messageMap = merged?.messageMap
    if (typeof messageMap === 'function') {
      return messageMap(params)
    }
    return params?.node?.data?.errorMessageMap as
      | Map<string, string[]>
      | undefined
  }

  init(params: ITooltipParams): void {
    this.setVisible(false)
    const table = ProcessingErrorTooltip.buildTable(
      ProcessingErrorTooltip.resolveMessageMap(params),
    )
    const body = this.getBody()
    if (table && body) {
      // Clear via textContent rather than innerHTML — same reason as above, and
      // it drops any previously-rendered children.
      body.textContent = ''
      body.appendChild(table)
      this.setVisible(true)
    }
  }

  getBody(): HTMLDivElement | null {
    return this.getGui().querySelector<HTMLDivElement>('.tooltip-inner')
  }
}
